import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCode } from 'lucide-react';

const PRODUCT_LABELS = {
  cards: 'Echo Card',
  letters: 'Letter',
  journals: 'Journal',
};

const ease = [0.22, 1, 0.36, 1];

const GiftPreview = ({ frontText, insideText, productType = 'letters', productId = 'default' }) => {
  const [stage, setStage] = useState(0);
  // 0 = sealed envelope, 1 = flap opening, 2 = letter rising, 3 = front revealed, 4 = inside revealed

  // Reset stage when message content changes (tone switch)
  useEffect(() => {
    setStage(0);
  }, [frontText, insideText]);

  // Auto-advance through the opening sequence
  useEffect(() => {
    if (stage === 1) {
      const t = setTimeout(() => setStage(2), 1200);
      return () => clearTimeout(t);
    }
    if (stage === 2) {
      const t = setTimeout(() => setStage(3), 1000);
      return () => clearTimeout(t);
    }
  }, [stage]);

  if (!frontText) return null;

  const label = PRODUCT_LABELS[productType] || 'Keepsake';

  const advance = () => {
    if (stage < 4) setStage((s) => s + 1);
  };

  const reset = () => setStage(0);

  const prompts = [
    'Tap to open your keepsake',
    '',
    '',
    'Tap to read inside',
    'Tap to close',
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.15, ease }}
      className="mt-8"
      data-testid={`gift-preview-${productId}`}
    >
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs tracking-[0.15em] uppercase text-[#C6A96B]/50 font-medium">
          Preview Your {label}
        </p>
        {stage > 0 && (
          <button
            onClick={reset}
            data-testid={`gift-reset-${productId}`}
            className="text-[10px] tracking-[0.12em] uppercase text-[#6B6259]/65 hover:text-[#C6A96B] transition-colors duration-300"
          >
            Reset
          </button>
        )}
      </div>

      {/* Unboxing Stage */}
      <div
        className="relative flex items-center justify-center cursor-pointer select-none"
        style={{ minHeight: '380px', perspective: '1200px' }}
        onClick={stage === 4 ? reset : advance}
        data-testid={`gift-stage-${stage}-${productId}`}
      >
        <AnimatePresence mode="wait">
          {/* ──── STAGE 0: Sealed Envelope ──── */}
          {stage === 0 && (
            <motion.div
              key="sealed"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.7, ease }}
              className="relative w-full max-w-[380px] mx-auto"
            >
              {/* Envelope body */}
              <div className="relative bg-gradient-to-b from-[#F5EFE5] to-[#EDE5D8] rounded-sm shadow-[0_12px_48px_rgba(11,31,58,0.08),0_2px_8px_rgba(198,169,107,0.06)] overflow-hidden"
                style={{ aspectRatio: '4/3' }}
              >
                {/* Envelope flap (closed) */}
                <div className="absolute top-0 left-0 right-0 overflow-hidden" style={{ height: '45%' }}>
                  <div
                    className="absolute inset-0 bg-gradient-to-b from-[#EAE2D3] to-[#F0E8DA]"
                    style={{
                      clipPath: 'polygon(0 0, 50% 70%, 100% 0)',
                    }}
                  />
                  {/* Inner flap shadow line */}
                  <div
                    className="absolute inset-0 opacity-10"
                    style={{
                      clipPath: 'polygon(0 0, 50% 70%, 100% 0)',
                      background: 'linear-gradient(to bottom, transparent 60%, rgba(11,31,58,0.15))',
                    }}
                  />
                </div>

                {/* Side flaps */}
                <div className="absolute bottom-0 left-0 right-0" style={{ height: '75%' }}>
                  <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(11,31,58,0.1) 0%, transparent 50%)',
                    }}
                  />
                </div>

                {/* Gold wax seal */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <motion.div
                    animate={{ scale: [1, 1.04, 1] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4B87A] via-[#C6A96B] to-[#B89A5C] shadow-[0_4px_16px_rgba(198,169,107,0.35)] flex items-center justify-center"
                  >
                    <div className="w-12 h-12 rounded-full border border-[#E8D5A8]/40 flex items-center justify-center">
                      <span className="text-[8px] tracking-[0.2em] uppercase text-[#0B1F3A]/60 font-semibold text-center leading-tight">
                        S2M
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* Subtle texture */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'6\' height=\'6\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h1v1H0z\' fill=\'%23000\'/%3E%3C/svg%3E")', backgroundSize: '6px 6px' }}
                />
              </div>

              {/* Prompt */}
              <motion.p
                animate={{ opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                className="text-center mt-6 text-xs tracking-[0.15em] text-[#C6A96B]/60 font-medium"
                data-testid={`gift-prompt-${productId}`}
              >
                {prompts[0]}
              </motion.p>
            </motion.div>
          )}

          {/* ──── STAGE 1+2: Envelope Opening + Letter Rising ──── */}
          {(stage === 1 || stage === 2) && (
            <motion.div
              key="opening"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease }}
              className="relative w-full max-w-[380px] mx-auto"
            >
              {/* Envelope body (back) */}
              <div className="relative bg-gradient-to-b from-[#F5EFE5] to-[#EDE5D8] rounded-sm shadow-[0_12px_48px_rgba(11,31,58,0.08)]"
                style={{ aspectRatio: '4/3' }}
              >
                {/* Flap opening animation */}
                <motion.div
                  className="absolute top-0 left-0 right-0 z-10"
                  style={{ height: '45%', transformOrigin: 'top center', transformStyle: 'preserve-3d' }}
                  initial={{ rotateX: 0 }}
                  animate={{ rotateX: stage >= 1 ? 180 : 0 }}
                  transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
                >
                  <div
                    className="absolute inset-0 bg-gradient-to-b from-[#EAE2D3] to-[#F0E8DA]"
                    style={{ clipPath: 'polygon(0 0, 50% 70%, 100% 0)', backfaceVisibility: 'hidden' }}
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-b from-[#FAF7F2] to-[#F0E8DA]"
                    style={{ clipPath: 'polygon(0 0, 50% 70%, 100% 0)', backfaceVisibility: 'hidden', transform: 'rotateX(180deg)' }}
                  />
                </motion.div>

                {/* Letter sliding up */}
                <motion.div
                  className="absolute inset-x-[10%] z-20"
                  style={{ bottom: '15%' }}
                  initial={{ y: 0, opacity: 0.8 }}
                  animate={{
                    y: stage >= 2 ? -140 : 0,
                    opacity: stage >= 2 ? 1 : 0.8,
                  }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: stage >= 2 ? 0 : 0.6 }}
                >
                  <div className="bg-white border border-[#C6A96B]/10 shadow-[0_8px_32px_rgba(11,31,58,0.06)] p-6 sm:p-8"
                    style={{ aspectRatio: '5/7' }}
                  >
                    <div className="flex flex-col items-center justify-center h-full opacity-70">
                      <div className="w-6 h-px bg-[#C6A96B]/20 mb-4" />
                      <p className="text-sm sm:text-base font-heading font-light text-[#0B1F3A]/50 text-center leading-[1.4]">
                        {frontText}
                      </p>
                      <div className="w-6 h-px bg-[#C6A96B]/20 mt-4" />
                    </div>
                  </div>
                </motion.div>

                <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'6\' height=\'6\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h1v1H0z\' fill=\'%23000\'/%3E%3C/svg%3E")', backgroundSize: '6px 6px' }}
                />
              </div>
            </motion.div>
          )}

          {/* ──── STAGE 3: Front of Letter Revealed ──── */}
          {stage === 3 && (
            <motion.div
              key="front"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, rotateY: -90 }}
              transition={{ duration: 0.8, ease }}
              className="relative w-full max-w-[360px] mx-auto"
            >
              <div
                className="relative bg-gradient-to-br from-[#FDFBF8] to-[#F5F0EA] border border-[#C6A96B]/12 shadow-[0_12px_48px_rgba(11,31,58,0.07),0_2px_6px_rgba(198,169,107,0.08)]"
                style={{ aspectRatio: productType === 'cards' ? '5/7' : '3/4' }}
                data-testid={`gift-front-${productId}`}
              >
                {/* Gold corner accents */}
                <div className="absolute top-0 left-0 w-12 h-12">
                  <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.3, ease }} className="absolute top-4 left-4 w-6 h-px bg-[#C6A96B]/30 origin-left" />
                  <motion.div initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.6, delay: 0.4, ease }} className="absolute top-4 left-4 w-px h-6 bg-[#C6A96B]/30 origin-top" />
                </div>
                <div className="absolute top-0 right-0 w-12 h-12">
                  <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.35, ease }} className="absolute top-4 right-4 w-6 h-px bg-[#C6A96B]/30 origin-right" />
                  <motion.div initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.6, delay: 0.45, ease }} className="absolute top-4 right-4 w-px h-6 bg-[#C6A96B]/30 origin-top" />
                </div>
                <div className="absolute bottom-0 left-0 w-12 h-12">
                  <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.4, ease }} className="absolute bottom-4 left-4 w-6 h-px bg-[#C6A96B]/30 origin-left" />
                  <motion.div initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.6, delay: 0.5, ease }} className="absolute bottom-4 left-4 w-px h-6 bg-[#C6A96B]/30 origin-bottom" />
                </div>
                <div className="absolute bottom-0 right-0 w-12 h-12">
                  <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.45, ease }} className="absolute bottom-4 right-4 w-6 h-px bg-[#C6A96B]/30 origin-right" />
                  <motion.div initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.6, delay: 0.55, ease }} className="absolute bottom-4 right-4 w-px h-6 bg-[#C6A96B]/30 origin-bottom" />
                </div>

                {/* Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 sm:p-12">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 0.4, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5, ease }}
                  >
                    <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 mb-6">
                      <path d="M50 88 L12 50 C4 42 4 28 12 20 C20 12 34 12 42 20 L50 28 L58 20 C66 12 80 12 88 20 C96 28 96 42 88 50 L50 88Z" stroke="#C6A96B" strokeWidth="2.5" fill="none"/>
                      <path d="M28 42 L50 58 L72 42" stroke="#C6A96B" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M28 42 L28 62 L72 62 L72 42" stroke="#C6A96B" strokeWidth="2" fill="none" strokeLinejoin="round"/>
                    </svg>
                  </motion.div>

                  <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6, ease }}
                    className="text-xl sm:text-2xl md:text-3xl font-heading font-light text-[#0B1F3A] text-center leading-[1.4] mb-4"
                  >
                    {frontText}
                  </motion.p>

                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 0.8, ease }}
                    className="w-10 h-px bg-[#C6A96B]/30 my-4"
                  />

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 1, ease }}
                    className="text-[10px] tracking-[0.25em] uppercase text-[#C6A96B]/40 font-medium"
                  >
                    Sent2ment
                  </motion.p>
                </div>

                <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'4\' height=\'4\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h1v1H0z\' fill=\'%23000\'/%3E%3C/svg%3E")', backgroundSize: '4px 4px' }}
                />
              </div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
                className="text-center mt-6 text-xs tracking-[0.15em] text-[#C6A96B]/60 font-medium"
              >
                {prompts[3]}
              </motion.p>
            </motion.div>
          )}

          {/* ──── STAGE 4: Inside Message Revealed ──── */}
          {stage === 4 && (
            <motion.div
              key="inside"
              initial={{ opacity: 0, rotateY: 90 }}
              animate={{ opacity: 1, rotateY: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.8, ease }}
              className="relative w-full max-w-[380px] mx-auto"
            >
              <div
                className="relative bg-gradient-to-br from-[#FDFBF8] to-[#F5F0EA] border border-[#C6A96B]/12 shadow-[0_12px_48px_rgba(11,31,58,0.07),0_2px_6px_rgba(198,169,107,0.08)]"
                style={{ minHeight: '340px' }}
                data-testid={`gift-inside-${productId}`}
              >
                {/* Left gold edge */}
                <motion.div
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 0.8, delay: 0.2, ease }}
                  className="absolute left-0 top-6 bottom-6 w-px bg-[#C6A96B]/15 origin-top"
                />

                <div className="p-8 sm:p-10 pl-8 sm:pl-12">
                  <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2, ease }}
                    className="text-base sm:text-lg font-heading font-light text-[#0B1F3A] mb-2 leading-[1.4]"
                  >
                    {frontText}
                  </motion.p>

                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.6, delay: 0.4, ease }}
                    className="w-8 h-px bg-[#C6A96B]/20 mb-5 origin-left"
                  />

                  <div className="max-h-[260px] overflow-y-auto pr-2">
                    {insideText.split('\n\n').map((paragraph, i) => (
                      <motion.p
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 + i * 0.15, ease }}
                        className="text-sm sm:text-base leading-[1.9] text-[#6B6259] font-heading italic mb-3 last:mb-0"
                      >
                        {paragraph}
                      </motion.p>
                    ))}
                  </div>
                </div>

                {/* QR indicator */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.3 }}
                  transition={{ duration: 0.6, delay: 1.2, ease }}
                  className="absolute bottom-4 right-4 flex items-center gap-2"
                >
                  <QrCode size={16} strokeWidth={1} className="text-[#C6A96B]" />
                  <span className="text-[9px] tracking-[0.1em] uppercase text-[#C6A96B] font-medium">
                    Spoken Echoes
                  </span>
                </motion.div>

                <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'4\' height=\'4\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h1v1H0z\' fill=\'%23000\'/%3E%3C/svg%3E")', backgroundSize: '4px 4px' }}
                />
              </div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                className="text-center mt-6 text-xs tracking-[0.15em] text-[#C6A96B]/60 font-medium"
              >
                {prompts[4]}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default GiftPreview;
